// Copyright (C) 2021-Present CITEC Inc. <https://citecsolutions.com/>
// All rights reserved
//
// This file is part of CITEC Inc. source code.
// This software framework contains the confidential and proprietary information
// of CITEC Inc., its affiliates, and its licensors. Your use of these
// materials is governed by the terms of the Agreement between your organization
// and CITEC Inc., and any unauthorized use is forbidden. Except as otherwise
// stated in the Agreement, this software framework is for your internal use
// only and may only be shared outside your organization with the prior written
// permission of CITEC Inc.
// CITEC Inc. source code can not be copied and/or distributed without the express
// permission of CITEC Inc.

import { type EtfFilters } from '@shared/data-access/interfaces/api/ai/etf';
import {
  type EtfFiltersResp,
  type MutualFundFiltersResp,
  type SearchETFResp,
  type SearchMutualFundsResp,
  type SearchResp,
  type StockFilters,
} from '@shared/data-access/interfaces/api/ai/screener';
import { type MutualFundFilters } from '@shared/data-access/interfaces/api/ai/mutual-funds';
import { type PaginationParams } from '@shared/data-access/interfaces/api/commons';
import { cleanPayload } from '@shared/data-access/utils/clean-payload';
import {
  etfSearchPayload,
  mutualFundsPayload,
} from '@shared/data-access/utils/payloads/ai';
import { aiApi } from '@shared/data-access/api-base/citec-ai-api';
import { SharedTagTypes } from '@shared/data-access/utils/constants';

export const screenerApi = aiApi.injectEndpoints({
  endpoints: build => ({
    searchScreener: build.query<
      SearchResp,
      { payload: StockFilters | EtfFilters | MutualFundFilters; assetType?: string }
    >({
      query: ({ payload, assetType = 'stock' }) => ({
        url: ['stock', 'etf'].includes(assetType)
          ? `/screener/${assetType}/search/`
          : `/screener/${assetType}/us/search/`,
        method: 'POST',
        body: payload,
      }),
      extraOptions: {
        includeCurrency: true,
      },
      providesTags: [
        { type: 'Screener', id: 'Search' },
        SharedTagTypes.CurrencyConversion,
      ],
    }),

    getEtfFilters: build.query<EtfFiltersResp, void>({
      query: () => ({
        url: '/screener/etf/search/settings/filters/',
        method: 'GET',
      }),
      providesTags: [{ type: 'Screener', id: 'EtfFilters' }],
    }),

    searchEtfs: build.query<
      SearchETFResp,
      { payload: EtfFilters; params?: PaginationParams; signal?: AbortSignal }
    >({
      query: ({ payload, params, signal }) => ({
        url: '/screener/etf/search/',
        method: 'POST',
        body: cleanPayload(payload, etfSearchPayload),
        params,
        signal,
      }),
      extraOptions: {
        includeCurrency: true,
      },
      providesTags: [
        { type: 'Screener', id: 'Etfs' },
        SharedTagTypes.CurrencyConversion,
      ],
    }),

    getMutualFundsFilters: build.query<MutualFundFiltersResp, void>({
      query: () => ({
        url: '/screener/mutual_fund/us/search/settings/filters/',
        method: 'GET',
      }),
      providesTags: [{ type: 'Screener', id: 'MutualFundFilters' }],
    }),

    searchMutualFunds: build.query<
      SearchMutualFundsResp,
      {
        payload: MutualFundFilters;
        params?: PaginationParams;
        signal?: AbortSignal;
      }
    >({
      query: ({ payload, params, signal }) => ({
        url: '/screener/mutual_fund/us/search/',
        method: 'POST',
        body: cleanPayload(payload, mutualFundsPayload),
        params,
        signal,
      }),
      extraOptions: {
        includeCurrency: true,
      },
      providesTags: [
        { type: 'Screener', id: 'MutualFunds' },
        SharedTagTypes.CurrencyConversion,
      ],
    }),
  }),
});
